type HostedRuntimeSessionPayload = {
  sessionId?: string
  runtimeUrl?: string
  expiresAt?: string
}

export const HOSTED_RUNTIME_REQUIRED_ROUTES = [
  "/smartwork/control-plane",
  "/project/upload",
  "/project/starter",
] as const
export const HOSTED_RUNTIME_PROJECT_CREATE_ROUTE = "/project/create" as const

export const HOSTED_RUNTIME_UNAVAILABLE_MESSAGE =
  "Hosted OpenAnalyst runtime session is unavailable. The hosted backend must proxy /runtime/openanalyst/session/:id/* to the OpenAnalyst runtime."

export class HostedOpenAnalystRuntimeUnavailableError extends Error {
  constructor(message = HOSTED_RUNTIME_UNAVAILABLE_MESSAGE) {
    super(message)
    this.name = "HostedOpenAnalystRuntimeUnavailableError"
  }
}

type HostedRuntimeRoute = (typeof HOSTED_RUNTIME_REQUIRED_ROUTES)[number] | typeof HOSTED_RUNTIME_PROJECT_CREATE_ROUTE

function objectValue(value: unknown) {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function stringValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

export function isHostedOpenAnalystAppHost(hostname: string) {
  const host = hostname.toLowerCase()
  return (
    host === "app.openanalyst.com" ||
    host === "app.basera.com" ||
    host === "cloud.openanalyst.com" ||
    host === "smartwork.arjun.tv" ||
    host === "smartwork.openanalyst.com"
  )
}

export function normalizeHostedRuntimeServerUrl(value: string) {
  return value.trim().replace(/\/+$/, "")
}

function hostedRuntimeSessionServerUrl(value: string | null) {
  if (!value?.trim()) return null
  try {
    const normalized = normalizeHostedRuntimeServerUrl(value)
    return /^\/runtime\/openanalyst\/session\/[^/]+\/?/.test(new URL(normalized).pathname) ? normalized : null
  } catch {
    return null
  }
}

function requestUrl(value: RequestInfo | URL) {
  if (value instanceof Request) return value.url
  return String(value)
}

function requestMethod(value: RequestInfo | URL, init?: RequestInit) {
  return (init?.method ?? (value instanceof Request ? value.method : "GET")).toUpperCase()
}

function runtimeSessionRequestUrl(value: RequestInfo | URL, serverUrl: string) {
  const target = requestUrl(value)
  return target === serverUrl || target.startsWith(`${serverUrl}/`) ? target : null
}

function rewriteRuntimeSessionRequest(value: RequestInfo | URL, from: string, to: string) {
  const target = runtimeSessionRequestUrl(value, from)
  if (!target) return null
  const next = `${to}${target.slice(from.length)}`
  if (value instanceof Request) return new Request(next, value)
  return next
}

export async function hostedRuntimeSessionMismatchResponse(response: Response) {
  if (response.status !== 401) return false
  const payload = objectValue(await response.clone().json().catch(() => null))
  const error = objectValue(payload.error)
  return (
    stringValue(error.code) === "HTTP_UNAUTHORIZED" &&
    stringValue(error.message) === "Runtime proxy session mismatch"
  )
}

export function createHostedRuntimeSessionRefreshFetch(input: {
  fetch?: typeof fetch
  getServerUrl: () => string | null
  refreshServerUrl: () => string | null | Promise<string | null>
  requireServerUrl?: () => boolean
}) {
  const fetchImpl = input.fetch ?? fetch
  return (async (requestInput, init) => {
    const response = await fetchImpl(requestInput, init)
    if (input.requireServerUrl && !input.requireServerUrl()) return response
    if (requestMethod(requestInput, init) !== "GET" && requestMethod(requestInput, init) !== "HEAD") return response
    const serverUrl = hostedRuntimeSessionServerUrl(input.getServerUrl())
    if (!serverUrl || !runtimeSessionRequestUrl(requestInput, serverUrl)) return response
    if (!(await hostedRuntimeSessionMismatchResponse(response))) return response
    const refreshed = hostedRuntimeSessionServerUrl(await Promise.resolve(input.refreshServerUrl()).catch(() => null))
    if (!refreshed || refreshed === serverUrl) return response
    const retryInput = rewriteRuntimeSessionRequest(requestInput, serverUrl, refreshed)
    return retryInput ? fetchImpl(retryInput, init) : response
  }) as typeof fetch
}

export async function createHostedOpenAnalystRuntimeServerUrl(input: {
  connectUrl: string
  publicOrigin: string
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  validate?: boolean
}) {
  const publicOrigin = normalizeHostedRuntimeServerUrl(input.publicOrigin)
  const parsed = new URL(input.connectUrl, publicOrigin)

  const sessionMatch = parsed.pathname.match(/^\/runtime\/openanalyst\/session\/([^/]+)\/?/)
  if (sessionMatch) {
    return validateHostedRuntimeServerUrl({
      serverUrl: normalizeHostedRuntimeServerUrl(new URL(parsed.pathname, publicOrigin).toString()),
      fetch: input.fetch,
      validate: input.validate,
    })
  }

  const connectMatch = parsed.pathname.match(/^\/runtime\/openanalyst\/connect\/([^/]+)\/?/)
  const tokenId = connectMatch?.[1]
  if (!tokenId) throw new Error("Hosted runtime connect URL did not include a route token id")

  const secret = parsed.hash.replace(/^#/, "").trim()
  if (!secret) throw new Error("Hosted runtime connect URL did not include a route token secret")

  const response = await input.fetch(`${publicOrigin}/runtime/openanalyst/connect/${encodeURIComponent(tokenId)}/session`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ secret }),
  })
  const payload = (await response.json().catch(() => ({}))) as HostedRuntimeSessionPayload
  if (!response.ok) throw new Error(`Hosted runtime session failed with status ${response.status}`)
  if (!payload.runtimeUrl?.trim()) throw new Error("Hosted runtime session response did not include runtimeUrl")

  const runtimeUrl = new URL(payload.runtimeUrl, publicOrigin)
  return validateHostedRuntimeServerUrl({
    serverUrl: normalizeHostedRuntimeServerUrl(`${runtimeUrl.origin}${runtimeUrl.pathname}${runtimeUrl.search}`),
    fetch: input.fetch,
    validate: input.validate,
  })
}

async function validateHostedRuntimeServerUrl(input: {
  serverUrl: string
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  validate?: boolean
}) {
  if (!input.validate) return input.serverUrl
  await requireHostedRuntimeRouteStatus({
    fetch: input.fetch,
    url: `${input.serverUrl}/global/health`,
    method: "GET",
    allowedStatuses: new Set([200]),
    failureLabel: "Health check",
  })
  return input.serverUrl
}

export async function validateHostedRuntimeContract(input: {
  serverUrl: string
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  requiredRoutes?: readonly HostedRuntimeRoute[]
}) {
  const serverUrl = normalizeHostedRuntimeServerUrl(input.serverUrl)
  await requireHostedRuntimeRouteStatus({
    fetch: input.fetch,
    url: `${serverUrl}/global/health`,
    method: "GET",
    allowedStatuses: new Set([200]),
    failureLabel: "Health check",
  })

  const response = await input
    .fetch(`${serverUrl}/global/capabilities`, {
      method: "GET",
      credentials: "include",
      headers: { accept: "application/json" },
    })
    .catch(() => null)
  if (!response) throw new HostedOpenAnalystRuntimeUnavailableError()
  if (!response.ok) {
    throw new HostedOpenAnalystRuntimeUnavailableError(
      `${HOSTED_RUNTIME_UNAVAILABLE_MESSAGE} Public session proxy check for /global/capabilities returned ${response.status}.`,
    )
  }

  const payload = objectValue(await response.json().catch(() => null))
  const routes = objectValue(payload.routes)
  const advertised = new Set(
    Object.values(routes)
      .map(stringValue)
      .filter((route): route is string => !!route),
  )
  const missing = (input.requiredRoutes ?? HOSTED_RUNTIME_REQUIRED_ROUTES).filter((route) => !advertised.has(route))
  if (missing.length) {
    throw new HostedOpenAnalystRuntimeUnavailableError(
      `Hosted OpenAnalyst runtime image is missing required web routes: ${missing.join(", ")}. Deploy a Daytona runtime image built from the latest feature/web-daytona branch.`,
    )
  }
  return payload
}

async function requireHostedRuntimeRouteStatus(input: {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>
  url: string
  method: "GET" | "POST"
  allowedStatuses: Set<number>
  failureLabel: string
  headers?: HeadersInit
  body?: BodyInit
}) {
  const response = await input
    .fetch(input.url, {
      method: input.method,
      credentials: "include",
      ...(input.headers ? { headers: input.headers } : {}),
      ...(input.body ? { body: input.body } : {}),
    })
    .catch(() => null)
  if (response && input.allowedStatuses.has(response.status)) return
  throw new HostedOpenAnalystRuntimeUnavailableError(
    response
      ? `${HOSTED_RUNTIME_UNAVAILABLE_MESSAGE} ${input.failureLabel} returned ${response.status}.`
      : HOSTED_RUNTIME_UNAVAILABLE_MESSAGE,
  )
}
